import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Zap, Clock, TrendingUp, CheckCircle2, ArrowRight, ArrowDown,
  Database, Mail, Calendar, Bell, GitBranch, Users, RefreshCw,
  Sparkles, BarChart3, Workflow, Target, Shield, Timer,
  Headphones, ChevronRight, Settings, Globe, MessageSquare,
  FileText, CreditCard, Bot, Smartphone, Send, Eye,
  UserCheck, Building2, Phone, Receipt, Link2, Layers,
  MousePointerClick, Play, PenTool, ExternalLink, Search,
  UserPlus, BrainCircuit, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import ServiceHero, { Highlight } from "./ServiceHero";

/* ───── SCROLL ANIMATION ───── */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function AnimateIn({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

/* ───── ANIMATED COUNTER ───── */
function AnimatedNumber({ value, suffix = "" }: { value: number; suffix?: string }) {
  const { ref, isVisible } = useInView();
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const start = Date.now();
    const duration = 2000;
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(value * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [isVisible, value]);
  return <span ref={ref}>{current}{suffix}</span>;
}

/* ───── BRAND ICON SVGS ───── */
const ZapierIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6"><path d="M12 0L14.59 8.41H24L16.71 13.59L19.29 22L12 16.82L4.71 22L7.29 13.59L0 8.41H9.41L12 0Z" fill="#FF4A00"/></svg>
);

const CalendlyIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5"><rect width="24" height="24" rx="5" fill="#006BFF"/><path d="M15.5 7.5a3.5 3.5 0 11-7 0M6 10h12v7a3 3 0 01-3 3H9a3 3 0 01-3-3v-7z" fill="none" stroke="#fff" strokeWidth="1.5"/><circle cx="12" cy="14" r="1.5" fill="#fff"/></svg>
);

const HubSpotIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5"><circle cx="12" cy="12" r="12" fill="#FF7A59"/><path d="M15 8.5V7a1.5 1.5 0 10-3 0v1.5A3.49 3.49 0 009.5 12 3.49 3.49 0 0012 15.5V17a1.5 1.5 0 103 0v-1.5A3.49 3.49 0 0017.5 12 3.49 3.49 0 0015 8.5zM13.5 14a2 2 0 110-4 2 2 0 010 4z" fill="#fff"/></svg>
);

const StripeIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5"><rect width="24" height="24" rx="5" fill="#635BFF"/><path d="M11.2 9.6c0-.7.6-1 1.5-1 1.3 0 3 .4 4.3 1.1V5.9c-1.4-.6-2.9-.8-4.3-.8C9.8 5.1 8 6.7 8 9.1c0 3.7 5.1 3.1 5.1 4.7 0 .8-.7 1.1-1.7 1.1-1.5 0-3.4-.6-4.9-1.4v3.8c1.7.7 3.3 1 4.9 1 2.9 0 4.9-1.4 4.9-3.9C16.3 10.3 11.2 11 11.2 9.6z" fill="#fff"/></svg>
);

const GmailIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5"><path d="M2 6l10 7 10-7v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" fill="#EA4335"/><path d="M22 6l-10 7L2 6V4a2 2 0 012-2h16a2 2 0 012 2v2z" fill="#FBBC04"/><path d="M2 6l10 7" stroke="#fff" strokeWidth=".5" fill="none"/><path d="M22 6l-10 7" stroke="#fff" strokeWidth=".5" fill="none"/><path d="M2 4a2 2 0 012-2h4l6 8L8 6H4a2 2 0 00-2 2v0z" fill="#C5221F"/><path d="M22 4a2 2 0 00-2-2h-4l-4 5 6 3h4a2 2 0 002-2V4z" fill="#34A853"/></svg>
);

const MetaIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5"><circle cx="12" cy="12" r="12" fill="#0081FB"/><path d="M7 15.5c0 .8.3 1.2.8 1.2.6 0 1.2-.7 2-2l1.5-2.5c1.2-2.1 2.2-3.2 3.7-3.2 2 0 3 1.7 3 4.2 0 2.8-1.4 4.8-3.6 4.8-1.1 0-2-.5-2.8-1.6l-.8 1.3c1 1.1 2.2 1.8 3.7 1.8 3 0 5.2-2.6 5.2-6.2 0-3.3-1.7-5.6-4.4-5.6-1.8 0-3 1.2-4.2 3.2L9.6 13c-.7 1.2-1 1.5-1.4 1.5-.3 0-.5-.3-.5-.8V9H6v5.5z" fill="#fff"/></svg>
);

const ChatGPTIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5"><circle cx="12" cy="12" r="12" fill="#10A37F"/><path d="M12 5a7 7 0 017 7c0 2.8-1.6 5.2-4 6.3V16a3 3 0 00-3-3H9.5A2.5 2.5 0 017 10.5v-.3A5.5 5.5 0 0112 5z" fill="none" stroke="#fff" strokeWidth="1.2"/><circle cx="12" cy="12" r="2" fill="#fff"/></svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5"><circle cx="12" cy="12" r="12" fill="#25D366"/><path d="M17.5 14.4c-.3-.15-1.7-.85-2-1-.3-.1-.5-.15-.7.15s-.8 1-.98 1.2c-.18.2-.36.22-.66.07-.3-.15-1.27-.47-2.42-1.5-.9-.8-1.5-1.8-1.67-2.1-.18-.3 0-.46.13-.6.13-.14.3-.36.45-.54.15-.18.2-.3.3-.5.1-.2.05-.38-.03-.53s-.7-1.7-.96-2.3c-.25-.6-.5-.52-.7-.53h-.6c-.2 0-.53.07-.8.37s-1.06 1.04-1.06 2.53 1.08 2.93 1.23 3.13c.15.2 2.13 3.25 5.16 4.56.72.31 1.28.5 1.72.64.72.23 1.38.2 1.9.12.58-.09 1.78-.73 2.03-1.43s.25-1.3.18-1.43c-.08-.13-.28-.2-.58-.36z" fill="#fff"/></svg>
);

/* ───── ZAP NODE ───── */
type ZapNodeProps = {
  icon: ReactNode;
  label: string;
  subtitle: string;
  accentColor: string;
  bgColor: string;
  number: number;
};

function ZapNode({ icon, label, subtitle, accentColor, bgColor, number }: ZapNodeProps) {
  return (
    <div className={`relative flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all duration-500 bg-card ${accentColor}`}>
      <div className="absolute -top-2.5 -left-2.5 w-5 h-5 rounded-full bg-chart-5 text-primary-foreground flex items-center justify-center text-[10px] font-bold">{number}</div>
      <div className={`w-9 h-9 rounded-lg ${bgColor} flex items-center justify-center shrink-0`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold truncate">{label}</p>
        <p className="text-[10px] text-muted-foreground truncate">{subtitle}</p>
      </div>
    </div>
  );
}

function ZapConnector() {
  return <div className="w-0.5 h-6 bg-chart-5/30 mx-auto relative">
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[6px] border-t-chart-5/30" />
  </div>;
}

const faqs = [
  { q: "¿Qué herramientas se pueden automatizar?", a: "Prácticamente cualquier herramienta que tenga API o integración con Zapier: CRM, email, WhatsApp, facturación, citas, formularios, plataformas publicitarias y muchas más." },
  { q: "¿Necesito tener ya las herramientas contratadas?", a: "Sí. Nosotros conectamos y automatizamos las herramientas que ya usas. Si necesitas recomendaciones, te asesoramos sobre las mejores opciones." },
  { q: "¿Cuánto tarda en implementarse?", a: "Depende de la complejidad. Automatizaciones simples se implementan en 1-2 días. Ecosistemas completos con múltiples herramientas pueden tardar 2-4 semanas." },
  { q: "¿Qué pasa si algo falla?", a: "Las automatizaciones incluyen gestión de errores y alertas. Si algo falla, el sistema notifica y tiene mecanismos de reintento automático." },
  { q: "¿Hay permanencia?", a: "No. Configuramos las automatizaciones y quedan funcionando. Puedes contratar mantenimiento mensual o gestión puntual." },
  { q: "¿Puedo modificar las automatizaciones después?", a: "Sí. Las automatizaciones quedan en tu cuenta de Zapier. Puedes modificarlas o solicitar cambios cuando lo necesites." },
  { q: "¿Cuántas automatizaciones incluye el servicio?", a: "No hay límite fijo. Diseñamos todas las que necesites según tus procesos. Desde flujos simples hasta ecosistemas completos con decenas de acciones." },
  { q: "¿Incluye el coste de Zapier?", a: "No. La suscripción a Zapier o la herramienta de automatización la gestionas tú. Nosotros nos encargamos del diseño, implementación y optimización." },
];

/* ───── ORBITAL TOOL HUB ───── */
function OrbitalHub() {
  const tools = [
    { icon: <HubSpotIcon />, name: "HubSpot", angle: 0 },
    { icon: <CalendlyIcon />, name: "Calendly", angle: 45 },
    { icon: <StripeIcon />, name: "Stripe", angle: 90 },
    { icon: <GmailIcon />, name: "Gmail", angle: 135 },
    { icon: <MetaIcon />, name: "Meta", angle: 180 },
    { icon: <ChatGPTIcon />, name: "ChatGPT", angle: 225 },
    { icon: <WhatsAppIcon />, name: "WhatsApp", angle: 270 },
    { icon: <CalendlyIcon />, name: "Slack", angle: 315 },
  ];

  const radius = 140; // px

  return (
    <div className="relative w-[360px] h-[360px] mx-auto">
      {/* Orbital ring */}
      <div className="absolute inset-[30px] rounded-full border-2 border-dashed border-chart-5/20 animate-[spin_40s_linear_infinite]" />
      
      {/* Connection lines */}
      {tools.map((tool, i) => {
        const rad = (tool.angle * Math.PI) / 180;
        const x = 180 + radius * Math.cos(rad);
        const y = 180 + radius * Math.sin(rad);
        return (
          <svg key={`line-${i}`} className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
            <line x1="180" y1="180" x2={x} y2={y} stroke="hsl(var(--chart-5))" strokeOpacity="0.15" strokeWidth="2" strokeDasharray="4 4" />
          </svg>
        );
      })}

      {/* Center — Zapier */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
        <div className="w-20 h-20 rounded-full bg-chart-5/10 border-2 border-chart-5/40 flex flex-col items-center justify-center shadow-glow">
          <ZapierIcon />
          <span className="text-[8px] font-bold text-chart-5 mt-0.5">ZAPIER</span>
        </div>
      </div>

      {/* Orbiting tools */}
      {tools.map((tool, i) => {
        const rad = (tool.angle * Math.PI) / 180;
        const x = 180 + radius * Math.cos(rad) - 24;
        const y = 180 + radius * Math.sin(rad) - 24;
        return (
          <div
            key={i}
            className="absolute z-10 group"
            style={{ left: x, top: y }}
          >
            <div className="w-12 h-12 rounded-xl bg-card border-2 border-border hover:border-chart-5/40 flex items-center justify-center shadow-md transition-all hover:scale-110 hover:shadow-lg cursor-default">
              {tool.icon}
            </div>
            <span className="block text-center text-[9px] font-semibold mt-1 text-muted-foreground group-hover:text-foreground transition-colors">{tool.name}</span>
          </div>
        );
      })}

      {/* Animated pulse rings */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border border-chart-5/10 animate-ping pointer-events-none" style={{ animationDuration: "3s" }} />
    </div>
  );
}

const AutomationDemo = () => {
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      icon: <Target className="w-5 h-5" />,
      title: "Ventas",
      desc: "Captura automática de leads, envío al CRM, asignación a vendedores, actualización del pipeline y seguimiento automático.",
      items: ["Captura automática de leads", "Envío de leads al CRM", "Asignación a vendedores", "Actualización del pipeline", "Seguimiento automático", "Secuencias de comunicación"],
    },
    {
      icon: <Calendar className="w-5 h-5" />,
      title: "Citas",
      desc: "Confirmación, recordatorios previos, mensajes de seguimiento post-cita y reprogramación automática.",
      items: ["Confirmación automática", "Recordatorios previos", "Mensajes de seguimiento", "Reprogramación automática"],
    },
    {
      icon: <MessageSquare className="w-5 h-5" />,
      title: "Comunicación",
      desc: "Secuencias automatizadas por email y WhatsApp, respuestas automáticas y flujos de contacto con clientes.",
      items: ["Email automático", "WhatsApp automático", "Formularios web", "Respuestas automáticas", "Flujos de seguimiento"],
    },
    {
      icon: <Receipt className="w-5 h-5" />,
      title: "Facturación",
      desc: "Generación y envío automático de facturas, registro de operaciones y gestión financiera automatizada.",
      items: ["Generación de facturas", "Envío automático", "Registro de operaciones", "Gestión financiera"],
    },
  ];

  return (
    <div className="space-y-32 overflow-hidden">

      {/* ━━━ HERO ━━━ */}
      <ServiceHero
        eyebrow="Automatización"
        title={<>Automatiza tu negocio, <Highlight>multiplica resultados</Highlight></>}
        description={<>Creamos un ecosistema digital que conecta todas las herramientas de tu empresa. Funciona <strong className="text-foreground font-semibold">24/7</strong>, nunca falla y elimina las tareas repetitivas para que tú te centres en crecer.</>}
        includes={["24/7 activo", "0 errores", "Ahorra +20h/semana", "+50 integraciones"]}
      />

      {/* ━━━ HIGHLIGHTS STRIP ━━━ */}
      <section className="max-w-5xl mx-auto">
        <AnimateIn>
          <div className="rounded-2xl border border-chart-5/20 bg-gradient-to-r from-chart-5/5 via-transparent to-chart-5/5 p-1">
            <div className="grid grid-cols-1 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border">
              {[
                { value: <AnimatedNumber value={20} suffix="+" />, label: "Horas ahorradas/semana" },
                { value: "0", label: "Errores manuales" },
                { value: "24/7", label: "Funcionando sin parar" },
                { value: "50+", label: "Herramientas integrables" },
              ].map((item, idx) => (
                <div key={idx} className="flex flex-col items-center justify-center p-5 md:p-6 text-center">
                  <div className="text-3xl font-bold text-chart-5 mb-1">{item.value}</div>
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ ZAPIER FLOW — CONCRETE ACTIONS ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-14">
            <Badge variant="secondary" className="mb-4"><Zap size={12} className="inline mr-1" />Ejemplo real</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Automatización de reuniones</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Así funciona un flujo real: desde que un cliente agenda una cita hasta el seguimiento completo, todo sin intervención manual.
            </p>
          </div>
        </AnimateIn>

        <AnimateIn delay={0.1}>
          <div className="max-w-lg mx-auto">
            <Card className="overflow-hidden border-chart-5/20 shadow-lg">
              {/* Zapier header */}
              <div className="px-5 py-3 border-b border-border bg-gradient-to-r from-chart-5/10 to-chart-5/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ZapierIcon />
                  <span className="text-sm font-semibold">Flujo de reuniones</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-chart-4 animate-pulse" />
                  <span className="text-[10px] text-chart-4 font-medium">Activo</span>
                </div>
              </div>

              <CardContent className="p-6 md:p-8">
                <div className="space-y-1">
                  {/* Step 1 */}
                  <ZapNode
                    number={1}
                    icon={<CalendlyIcon />}
                    label="Nueva reunión agendada"
                    subtitle="Un cliente reserva cita en Calendly"
                    accentColor="border-[#006BFF]/30"
                    bgColor="bg-[#006BFF]/10"
                  />
                  <ZapConnector />

                  {/* Step 2 */}
                  <ZapNode
                    number={2}
                    icon={<HubSpotIcon />}
                    label="Buscar lead en el CRM"
                    subtitle="Comprobar si el contacto ya existe"
                    accentColor="border-[#FF7A59]/30"
                    bgColor="bg-[#FF7A59]/10"
                  />
                  <ZapConnector />

                  {/* Step 3 — Split */}
                  <div className="px-4 py-2.5 rounded-xl bg-chart-5/10 border-2 border-chart-5/30 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <GitBranch size={14} className="text-chart-5" />
                      <span className="text-xs font-semibold text-chart-5">¿Existe el contacto?</span>
                    </div>
                  </div>

                  {/* Two paths */}
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    {/* YES */}
                    <div>
                      <Badge className="bg-chart-4/10 text-chart-4 border-chart-4/20 text-[10px] mb-2 w-full justify-center">✓ Sí existe</Badge>
                      <div className="p-3 rounded-xl border border-chart-4/20 bg-chart-4/5 text-center">
                        <RefreshCw size={14} className="mx-auto text-chart-4 mb-1" />
                        <p className="text-[10px] font-semibold">Actualizar datos</p>
                        <p className="text-[9px] text-muted-foreground">en el CRM</p>
                      </div>
                    </div>
                    {/* NO */}
                    <div>
                      <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px] mb-2 w-full justify-center">✗ No existe</Badge>
                      <div className="p-3 rounded-xl border border-primary/20 bg-primary/5 text-center">
                        <UserPlus size={14} className="mx-auto text-primary mb-1" />
                        <p className="text-[10px] font-semibold">Crear contacto</p>
                        <p className="text-[9px] text-muted-foreground">nuevo en el CRM</p>
                      </div>
                    </div>
                  </div>

                  <ZapConnector />

                  {/* Step 4 */}
                  <ZapNode
                    number={4}
                    icon={<ChatGPTIcon />}
                    label="Formatear datos con IA"
                    subtitle="ChatGPT prepara el mensaje personalizado"
                    accentColor="border-[#10A37F]/30"
                    bgColor="bg-[#10A37F]/10"
                  />
                  <ZapConnector />

                  {/* Step 5 */}
                  <ZapNode
                    number={5}
                    icon={<WhatsAppIcon />}
                    label="Enviar mensaje de confirmación"
                    subtitle="WhatsApp al cliente con los detalles"
                    accentColor="border-[#25D366]/30"
                    bgColor="bg-[#25D366]/10"
                  />
                  <ZapConnector />

                  {/* Step 6 */}
                  <ZapNode
                    number={6}
                    icon={<GmailIcon />}
                    label="Enviar email al equipo"
                    subtitle="Notificar al vendedor asignado"
                    accentColor="border-[#EA4335]/30"
                    bgColor="bg-[#EA4335]/10"
                  />
                  <ZapConnector />

                  {/* Step 7 */}
                  <ZapNode
                    number={7}
                    icon={<Clock size={16} className="text-chart-5" />}
                    label="Esperar 24h antes de la cita"
                    subtitle="Delay programado automáticamente"
                    accentColor="border-chart-5/30"
                    bgColor="bg-chart-5/10"
                  />
                  <ZapConnector />

                  {/* Step 8 */}
                  <ZapNode
                    number={8}
                    icon={<WhatsAppIcon />}
                    label="Enviar recordatorio"
                    subtitle="WhatsApp: 'Tu reunión es mañana a las...'"
                    accentColor="border-[#25D366]/30"
                    bgColor="bg-[#25D366]/10"
                  />
                </div>

                {/* Summary */}
                <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-chart-5/10 to-chart-4/10 border border-chart-5/20">
                  <div className="flex flex-wrap items-center justify-center gap-6 text-center">
                    <div>
                      <div className="text-2xl font-bold text-chart-5">8</div>
                      <div className="text-[10px] text-muted-foreground">Pasos automatizados</div>
                    </div>
                    <div className="w-px h-8 bg-border hidden sm:block" />
                    <div>
                      <div className="text-2xl font-bold text-chart-5">5</div>
                      <div className="text-[10px] text-muted-foreground">Herramientas conectadas</div>
                    </div>
                    <div className="w-px h-8 bg-border hidden sm:block" />
                    <div>
                      <div className="text-2xl font-bold text-chart-4">0 min</div>
                      <div className="text-[10px] text-muted-foreground">Trabajo manual</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ CÓMO FUNCIONA — REPLACES ANÁLISIS Y DISEÑO ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-14">
            <Badge variant="secondary" className="mb-4">Nuestro proceso</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">De procesos manuales a sistemas automáticos</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Estudiamos cómo trabaja tu empresa, identificamos todo lo que se puede automatizar y lo construimos paso a paso.
            </p>
          </div>
        </AnimateIn>

        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {[
            {
              icon: <Search className="w-6 h-6" />,
              title: "1. Escuchamos y mapeamos",
              desc: "Nos reunimos contigo para entender cada proceso: quién hace qué, con qué herramienta y con qué frecuencia. Identificamos las tareas que más tiempo consumen y las que más errores generan.",
              color: "text-chart-5",
              bg: "bg-chart-5/10",
              border: "border-chart-5/15",
            },
            {
              icon: <Workflow className="w-6 h-6" />,
              title: "2. Diseñamos los flujos",
              desc: "Creamos el mapa completo de automatizaciones: qué herramientas se conectan, qué datos se pasan entre ellas y qué acciones se ejecutan. Todo personalizado para tu negocio.",
              color: "text-primary",
              bg: "bg-primary/10",
              border: "border-primary/15",
            },
            {
              icon: <Zap className="w-6 h-6" />,
              title: "3. Construimos y activamos",
              desc: "Implementamos cada automatización, la probamos a fondo y la activamos. Desde el primer día, tu negocio trabaja solo mientras tú te enfocas en lo que importa.",
              color: "text-chart-4",
              bg: "bg-chart-4/10",
              border: "border-chart-4/15",
            },
          ].map((step, idx) => (
            <AnimateIn key={idx} delay={idx * 0.1}>
              <Card className={`h-full ${step.border} hover:shadow-md transition-shadow`}>
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-2xl ${step.bg} flex items-center justify-center ${step.color} mb-4`}>
                    {step.icon}
                  </div>
                  <h4 className="font-bold text-lg mb-2">{step.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                </CardContent>
              </Card>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ━━━ ORQUESTACIÓN — ORBITAL HUB ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-14">
            <Badge variant="secondary" className="mb-4">Ecosistema conectado</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Todas tus herramientas, un solo sistema</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Conectamos todas las aplicaciones de tu empresa para que intercambien datos y ejecuten acciones automáticamente. Un ecosistema que trabaja <span className="text-foreground font-semibold">24/7 sin fallos</span>.
            </p>
          </div>
        </AnimateIn>

        <AnimateIn delay={0.1}>
          <div className="max-w-4xl mx-auto">
            {/* Desktop orbital */}
            <div className="hidden md:block">
              <OrbitalHub />
            </div>
            
            {/* Mobile — grid */}
            <div className="md:hidden">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-chart-5/10 border-2 border-chart-5/40 flex flex-col items-center justify-center">
                  <ZapierIcon />
                  <span className="text-[7px] font-bold text-chart-5">ZAPIER</span>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { icon: <HubSpotIcon />, name: "HubSpot" },
                  { icon: <CalendlyIcon />, name: "Calendly" },
                  { icon: <StripeIcon />, name: "Stripe" },
                  { icon: <GmailIcon />, name: "Gmail" },
                  { icon: <MetaIcon />, name: "Meta" },
                  { icon: <ChatGPTIcon />, name: "ChatGPT" },
                  { icon: <WhatsAppIcon />, name: "WhatsApp" },
                  { icon: <CalendlyIcon />, name: "Slack" },
                ].map((tool, i) => (
                  <div key={i} className="flex flex-col items-center gap-1 p-3 rounded-xl border border-border">
                    {tool.icon}
                    <span className="text-[9px] font-medium text-muted-foreground">{tool.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Value prop below */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: <Clock size={20} />, title: "Ahorra tiempo y dinero", desc: "Elimina horas de trabajo manual repetitivo cada semana." },
                { icon: <Shield size={20} />, title: "Funciona 24/7 sin fallos", desc: "Tus procesos trabajan de noche, fines de semana y festivos." },
                { icon: <Layers size={20} />, title: "Escala sin límites", desc: "Desde 5 hasta 500 automatizaciones. Crece sin contratar más." },
              ].map((v, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                  <div className="w-10 h-10 rounded-xl bg-chart-5/10 flex items-center justify-center text-chart-5 shrink-0">{v.icon}</div>
                  <div>
                    <h5 className="text-sm font-semibold mb-0.5">{v.title}</h5>
                    <p className="text-xs text-muted-foreground">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ SERVICIOS — INTERACTIVE TABS ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-14">
            <Badge variant="secondary" className="mb-4">Qué automatizamos</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Automatizaciones por área</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Cada área de tu negocio puede beneficiarse de la automatización.
            </p>
          </div>
        </AnimateIn>

        <AnimateIn delay={0.1}>
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {services.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveService(idx)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border transition-all duration-300 text-sm ${
                    activeService === idx
                      ? "border-chart-5/40 bg-chart-5/5 shadow-md scale-105 text-chart-5"
                      : "border-border bg-card hover:border-chart-5/20 hover:bg-muted/50"
                  }`}
                >
                  {s.icon}
                  <span className="font-medium">{s.title}</span>
                </button>
              ))}
            </div>

            <Card className="overflow-hidden border-chart-5/10" key={activeService}>
              <CardContent className="p-8 md:p-10 bg-gradient-to-br from-chart-5/5 to-transparent">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-chart-5/10 border border-chart-5/20 flex items-center justify-center text-chart-5">
                    {services[activeService].icon}
                  </div>
                  <div>
                    <h4 className="font-bold text-xl">Automatización de {services[activeService].title}</h4>
                    <Badge variant="secondary" className="text-xs mt-1">Flujo automatizado</Badge>
                  </div>
                </div>
                <p className="text-muted-foreground mb-6 max-w-2xl">{services[activeService].desc}</p>

                <div className="grid sm:grid-cols-2 gap-3">
                  {services[activeService].items.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/30 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-chart-5/10 flex items-center justify-center text-chart-5 shrink-0">
                        <CheckCircle2 size={14} />
                      </div>
                      <span className="text-sm font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ OBJETIVO ━━━ */}
      <section className="max-w-4xl mx-auto">
        <AnimateIn>
          <Card className="border-chart-5/10 bg-gradient-to-br from-chart-5/5 to-transparent">
            <CardContent className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-chart-5/10 flex items-center justify-center text-chart-5">
                  <Zap className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-xl">El objetivo</h4>
                  <p className="text-sm text-muted-foreground">Lo que conseguimos con este servicio</p>
                </div>
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Transformar los procesos de tu negocio en <span className="text-foreground font-semibold">sistemas automatizados</span> que 
                funcionan de forma continua <span className="text-foreground font-semibold">24 horas al día, 7 días a la semana</span>, 
                reduciendo trabajo manual, eliminando errores y mejorando la <span className="text-foreground font-semibold">eficiencia operativa</span>. 
                Un ecosistema que nunca descansa, nunca falla y te ahorra tiempo y dinero.
              </p>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>

      {/* ━━━ ¿PARA QUIÉN ES? ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-14">
            <Badge variant="secondary" className="mb-4">¿Es para ti?</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">¿Para quién es este servicio?</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Empresas que pierden tiempo en tareas repetitivas y quieren escalar sin aumentar equipo.
            </p>
          </div>
        </AnimateIn>

        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
          <AnimateIn delay={0.1}>
            <Card className="h-full border-chart-5/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-chart-5/10 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-chart-5" />
                  </div>
                  <h4 className="font-bold text-lg">Ideal para</h4>
                </div>
                <div className="space-y-4">
                  {[
                    { icon: <Building2 className="w-4 h-4" />, title: "Pymes en crecimiento", desc: "Empresas que necesitan escalar operaciones sin contratar más personal." },
                    { icon: <Users className="w-4 h-4" />, title: "Equipos comerciales", desc: "Equipos de ventas que pierden tiempo en tareas manuales de seguimiento." },
                    { icon: <Workflow className="w-4 h-4" />, title: "Negocios con procesos repetitivos", desc: "Empresas con flujos manuales que se pueden automatizar." },
                    { icon: <BrainCircuit className="w-4 h-4" />, title: "Empresas con múltiples herramientas", desc: "Negocios que usan varias apps desconectadas entre sí." },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <div className="w-8 h-8 rounded-lg bg-chart-5/10 flex items-center justify-center text-chart-5 shrink-0 mt-0.5">
                        {item.icon}
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">{item.title}</h5>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </AnimateIn>

          <AnimateIn delay={0.2}>
            <Card className="h-full border-destructive/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-destructive" />
                  </div>
                  <h4 className="font-bold text-lg">No es para ti si…</h4>
                </div>
                <div className="space-y-4">
                  {[
                    "No tienes procesos definidos que se puedan sistematizar todavía.",
                    "Tu negocio no tiene volumen suficiente para justificar la automatización.",
                    "No usas herramientas digitales en tu operativa actual.",
                    "Prefieres mantener todos los procesos de forma manual.",
                  ].map((text, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0 mt-0.5">
                        <AlertCircle className="w-4 h-4 text-destructive" />
                      </div>
                      <p className="text-sm text-muted-foreground">{text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </AnimateIn>
        </div>
      </section>

      {/* ━━━ ¿POR QUÉ CON VIBRA? ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-14">
            <Badge variant="secondary" className="mb-4">Comparativa</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">¿Por qué con Vibra?</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Diferencias reales entre automatizar tu negocio solo o con un equipo especializado.
            </p>
          </div>
        </AnimateIn>

        <AnimateIn delay={0.1}>
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="grid grid-cols-3 border-b border-border">
                  <div className="p-4 md:p-6" />
                  <div className="p-4 md:p-6 text-center border-x border-border bg-muted/30">
                    <span className="text-sm font-semibold text-muted-foreground">Hacerlo tú</span>
                  </div>
                  <div className="p-4 md:p-6 text-center bg-chart-5/5">
                    <span className="text-sm font-bold text-chart-5">Con Vibra</span>
                  </div>
                </div>
                {[
                  { feature: "Análisis de procesos automatizables", solo: false, vibra: true },
                  { feature: "Diseño de flujos personalizados", solo: false, vibra: true },
                  { feature: "Integración con +50 herramientas", solo: false, vibra: true },
                  { feature: "Chatbots con IA avanzada", solo: false, vibra: true },
                  { feature: "Monitorización 24/7", solo: false, vibra: true },
                  { feature: "Optimización y mantenimiento continuo", solo: false, vibra: true },
                  { feature: "Tiempo dedicado por tu parte", solo: true, vibra: false },
                  { feature: "Conocimiento técnico de APIs", solo: true, vibra: false },
                ].map((row, idx) => (
                  <div key={idx} className={`grid grid-cols-3 border-b border-border last:border-0 ${idx % 2 === 0 ? "" : "bg-muted/10"}`}>
                    <div className="p-4 md:p-5 flex items-center">
                      <span className="text-sm font-medium">{row.feature}</span>
                    </div>
                    <div className="p-4 md:p-5 flex items-center justify-center border-x border-border">
                      <div className="w-7 h-7 rounded-full bg-destructive/10 flex items-center justify-center">
                        <AlertCircle size={14} className="text-destructive" />
                      </div>
                    </div>
                    <div className="p-4 md:p-5 flex items-center justify-center bg-chart-5/5">
                      <div className="w-7 h-7 rounded-full bg-chart-5/10 flex items-center justify-center">
                        <CheckCircle2 size={14} className="text-chart-5" />
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ FAQs ━━━ */}
      <section className="max-w-3xl mx-auto">
        <AnimateIn>
          <div className="text-center mb-14">
            <Badge variant="secondary" className="mb-4">FAQs</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Preguntas frecuentes</h3>
          </div>
        </AnimateIn>

        <AnimateIn delay={0.1}>
          <Card>
            <CardContent className="p-2 md:p-4">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, idx) => (
                  <AccordionItem key={idx} value={`faq-${idx}`} className="border-border/50">
                    <AccordionTrigger className="text-left text-sm md:text-base px-3 hover:no-underline hover:text-chart-5 transition-colors">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground px-3">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>

      {/* ━━━ CTA FINAL ━━━ */}
      <section className="relative max-w-4xl mx-auto">
        <div className="absolute -inset-10 bg-chart-5/5 rounded-[3rem] blur-3xl pointer-events-none" />

        <AnimateIn>
          <Card className="border-chart-5/20 overflow-hidden relative">
            <CardContent className="p-10 md:p-16 text-center relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-chart-5/10 flex items-center justify-center text-chart-5 mx-auto mb-6">
                <Zap className="w-8 h-8" />
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
                ¿Listo para automatizar<br className="hidden md:block" /> tu negocio?
              </h3>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
                Análisis, diseño, implementación y optimización. Todo gestionado por nosotros.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                <Button size="lg" className="gap-2 text-base px-8 shadow-glow">
                  Agendar reunión
                  <ArrowRight size={16} />
                </Button>
                <Button size="lg" variant="outline" className="gap-2 text-base px-8">
                  Solicitar propuesta
                </Button>
              </div>
              <div className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-chart-5" /> Sin permanencia</span>
                <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-chart-5" /> 24/7 activo</span>
                <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-chart-5" /> +50 integraciones</span>
              </div>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>
    </div>
  );
};

export default AutomationDemo;
